import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { InventoryTransferDetailModel } from 'src/app/models/inventory/inventory-transfer-detail.model';
import { InventoryTransferModel } from 'src/app/models/inventory/inventory-transfer.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { RentalStockModalModel } from 'src/app/models/tables/rental/rental-stock-modal.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { InventoryTransferDetailService } from 'src/app/services/inventory/inventory-transfer-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-inventory-transfer-detail',
  templateUrl: './register-inventory-transfer-detail.component.html',
  styleUrls: ['./register-inventory-transfer-detail.component.scss'],
  standalone: false
})
export class RegisterInventoryTransferDetailComponent implements OnInit {

  @ViewChild('articleSelection') articleSelectionModalComponent?: CustomModalComponent | null = null;

  @Input() inventoryTransferModel: InventoryTransferModel = new InventoryTransferModel();
  @Input() inventoryTransferDetailModel: InventoryTransferDetailModel = new InventoryTransferDetailModel();
  @Input() isFormViewOpen: boolean = false;
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  articleId: number = 0;
  ascendantArticle: boolean = true;
  codeNameArticle: string = '';
  columnSortArticle: string | null = null;
  dataArticle: PagedResponseModel<RentalStockModalModel> = new PagedResponseModel<RentalStockModalModel>();
  inventoryTransferDetailId: number = 0;
  filtersArticle: ColumnResponseModel[] = [];
  formInventoryTransferDetail: FormGroup = new FormGroup({});
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: true,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: []
  });
  limitArticle: number = 5;
  output$ = new Subject<any>();
  pageArticle: number = 1;
  rentalStockArticle: number = 0;
  rentalStockForm: number = 0;
  subscription: Subscription;
  componentTableSelection = CustomTableSelectComponent;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private inventoryTransferDetailService: InventoryTransferDetailService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(this.inventoryTransferDetailModel);
  }

  ngOnInit() {
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticle(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    const inventoryTransferDetailModel = changes['inventoryTransferDetailModel'];
    const isFormViewOpen = changes['isFormViewOpen'];
    if (inventoryTransferDetailModel) {
      if (this.inventoryTransferModel.inventoryTransferId !== 0) {
        await this.getArticlesForDetailsAsync();
      }
      if (this.inventoryTransferDetailModel.inventoryTransferDetailId === 0) {
        this.inventoryTransferDetailId = 0;
        this.codeNameArticle = '';
        this.rentalStockArticle = 0;
        this.inventoryTransferDetailModel = new InventoryTransferDetailModel();
        if (isFormViewOpen && !isFormViewOpen.firstChange) {
          this.openArticleSelectionModal();
        }
      } else {
        this.getArticle(this.inventoryTransferDetailModel.articleId);
        this.inventoryTransferDetailId = this.inventoryTransferDetailModel.inventoryTransferDetailId;
        this.rentalStockArticle = this.dataArticle.items.find(item => item.articleId === this.inventoryTransferDetailModel.articleId)?.stock || 0;
      }
      this.validateForm(this.inventoryTransferDetailModel);
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async changeQuantity(quantity: number) {
    if (quantity < 0) {
      this.toastHandlerService.showToast(
        'error',
        'La cantidad no puede ser negativa.'
      );
      this.rentalStockForm = this.rentalStockArticle + this.inventoryTransferDetailModel.quantity;
      setTimeout(() => this.formInventoryTransferDetail.get('quantity')?.patchValue(this.inventoryTransferDetailModel.quantity), 100);
      return;
    }
    if (this.inventoryTransferDetailModel.inventoryTransferDetailId === 0) {
      if (quantity > this.rentalStockArticle) {
        this.toastHandlerService.showToast('error', 'La cantidad no puede ser mayor a la existencia.');
        setTimeout(() => this.formInventoryTransferDetail.get('quantity')?.patchValue(this.inventoryTransferDetailModel.quantity), 100);
        return;
      }
      this.rentalStockForm = this.rentalStockArticle - quantity;
    } else {
      if (quantity > (this.rentalStockArticle + this.inventoryTransferDetailModel.quantity)) {
        this.toastHandlerService.showToast('error', 'La cantidad no puede ser mayor a la existencia.');
        setTimeout(() => this.formInventoryTransferDetail.get('quantity')?.patchValue(this.inventoryTransferDetailModel.quantity), 100);
        return;
      }
      this.rentalStockForm = this.rentalStockArticle + this.inventoryTransferDetailModel.quantity - quantity;
    }
  }

  clearForm() {
    this.inventoryTransferDetailId = 0;
    this.codeNameArticle = '';
    this.rentalStockArticle = 0;
    this.inventoryTransferDetailModel = new InventoryTransferDetailModel();
    this.validateForm(this.inventoryTransferDetailModel);
    this.isFormView.emit(false);
  }

  async getArticle(articleId: number | null) {
    if (articleId != null && articleId != 0) {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      if (article) {
        this.articleId = articleId;
        this.codeNameArticle = `${article.code} - ${article.name}`;
      } else {
        this.codeNameArticle = 'Error al buscar el elemento. Por favor refresque la página.';
        return;
      }
    } else {
      this.articleId = 0;
      this.codeNameArticle = '';
    }
  }

  async getArticlesForDetailsAsync() {
    try {
      this.dataArticle = await firstValueFrom(this.inventoryTransferDetailService.getArticlesForDetailsAsync(this.inventoryTransferModel.inventoryTransferId,
        this.pageArticle, this.limitArticle, this.columnSortArticle, this.ascendantArticle, this.filtersArticle)
      );
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getColumnsArticle() {
    try {
      if (this.headerArticle.length === 0) {
        this.headerArticle = await firstValueFrom(this.inventoryTransferDetailService.getColumnsForArticles());
      }
    } catch (error) {
      console.error('Error al buscar las columnas de artículos.', error);
    }
  }

  async getInventoryTransferDetailByKeys(articleId: number) {
    try {
      this.inventoryTransferDetailModel = await firstValueFrom(this.inventoryTransferDetailService.getInventoryTransferDetailByKeys(this.inventoryTransferModel.inventoryTransferId, articleId));
      this.validateForm(this.inventoryTransferDetailModel);
      this.inventoryTransferDetailId = this.inventoryTransferDetailModel.inventoryTransferDetailId;
    } catch (error) {
      console.error('Error al buscar el detalle por claves.', error);
    }
  }

  async handleSelectedItem(selectedItem: any) {
    if (selectedItem && selectedItem.length === 1) {
      this.isFormView.emit(true);
      const articleId = parseInt(selectedItem[0]?.articleId);
      this.getArticle(articleId);
      this.rentalStockArticle = this.dataArticle.items.find(item => item.articleId === articleId)?.stock || 0;
      this.getInventoryTransferDetailByKeys(articleId);
    }
    if (selectedItem && selectedItem.length > 1) {
      await firstValueFrom(this.inventoryTransferDetailService.createSomeDetails(selectedItem, this.inventoryTransferModel.inventoryTransferId));
      this.clearForm();
      this.updateListDetail.emit(true);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async onPageChangeArticle(event: {
    page: number;
    pageSize: number;
    sortField: string | undefined;
    sortOrder: number;
  }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    this.getArticlesForDetailsAsync();
  }

  async openArticleSelectionModal() {
    this.filtersArticle = [];
    await Promise.all([
      await this.getColumnsArticle(),
      this.getArticlesForDetailsAsync(),
    ]);
    this.updateInputs();

    if (this.articleSelectionModalComponent && this.headerArticle) {
      this.articleSelectionModalComponent.show();
    } else {
      this.toastHandlerService.showToast(
        'error',
        'No se pudieron cargar los artículos. Por favor, inténtelo de nuevo.'
      );
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (
      valueFilter === null ||
      valueFilter === undefined ||
      valueFilter === '' ||
      valueFilter === 0
    ) {
      this.filtersArticle = this.filtersArticle.filter(
        (f) => f.propertyName !== propertyName
      );
    } else {
      const existingFilter = this.filtersArticle.find(
        (f) => f.propertyName === propertyName
      );
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter(
      (value, index, self) =>
        index ===
        self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async send() {
    this.formInventoryTransferDetail.enable();
    this.formInventoryTransferDetail.controls['inventoryTransferId'].patchValue(
      this.inventoryTransferDetailModel.inventoryTransferId
    );
    this.formInventoryTransferDetail.controls['articleId'].patchValue(this.articleId);
    Object.keys(this.formInventoryTransferDetail.controls).forEach((key) => {
      const control = this.formInventoryTransferDetail.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formInventoryTransferDetail.valid) {
      const modelInventoryTransferDetail = this.formInventoryTransferDetail.value as InventoryTransferDetailModel;
      if (modelInventoryTransferDetail.quantity === 0) {
        this.toastHandlerService.showToast('warning', 'La cantidad no puede ser cero.');
        return;
      }

      if (this.inventoryTransferDetailId === 0) {
        try {
          await firstValueFrom(this.inventoryTransferDetailService.createInventoryTransferDetail(modelInventoryTransferDetail));
          this.updateListDetail.emit(true);
          this.toastHandlerService.showToast('success', 'El detalle del traslado fue creado exitosamente.');
          this.clearForm();
        } catch (error) {
          console.error('Error al crear el detalle del traslado.', error);
        }
      } else {
        this.inventoryTransferDetailModel.quantity = modelInventoryTransferDetail.quantity;

        try {
          await firstValueFrom(this.inventoryTransferDetailService.updateInventoryTransferDetail(this.inventoryTransferDetailModel));
          this.updateListDetail.emit(true);
          this.toastHandlerService.showToast('success', 'El detalle de la devolución fue modificado exitosamente.');
          this.clearForm();
        } catch (error) {
          console.error('Error to update devolution detail.', error);
        }

      }
    } else {
      this.showValidationMessages();
      this.validateForm(this.inventoryTransferDetailModel);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      quantity: {
        required: 'El campo cantidad es obligatorio.',
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInventoryTransferDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateInputs() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }

  validateForm(inventoryTransferDetail: InventoryTransferDetailModel) {
    this.formInventoryTransferDetail = this.formBuilder.group({
      inventoryTransferId: [inventoryTransferDetail.inventoryTransferId, [Validators.required, Validators.min(1)]],
      articleId: [inventoryTransferDetail.articleId, [Validators.required, Validators.min(1)]],
      quantity: [inventoryTransferDetail.quantity]
    });
    this.rentalStockForm = this.rentalStockArticle;
  }
}
